import {
  DialogContent,
  FormControl,
  MenuItem,
  Select,
  Typography,
  useTheme,
  Box,
  Grid,
} from "@mui/material";
import WarningOutlinedIcon from '@mui/icons-material/WarningOutlined';
import styles from "./EVInstantaneousStopEventDialog.module.scss";
import { ScheduledEvent } from "types";
import { useEffect, useState } from "react";
import { EventManagementAPI } from "apis/eventManagementAPI";
import { useGlobalConfig } from "context/GlobalContext";
import { EVENTS } from "framework/events/EventSystem";
import {
  BglDialog,
  BglDialogActions,
  BglDialogTitle,
} from "components/dialog/BglDialogStyles";
import { BglCloseButton, BglOutlinedButton, BglPrimaryButton } from "components/button/BglButtons";
import { useRequestApiData } from "hooks";
import { GetEVLoadAPI } from "types/evLoadManagement/EVLoadManagementInterface";
import { EVLoadManagementAPI } from "apis/evLoadManagementAPI";
import { EV_INST_EVENT_STRINGS, EV_LOAD_STRINGS } from "types/translations/EVLoadManagementStrings";
import { toast } from "react-toastify";
import { EVLoadManagement } from "../dashboard/EVLoadManagement/EVLoadManagement";
import { BglLoading } from "components/loading/BglLoading";
import { EVLOAD_ACTIONS } from "store/actions/evLoadActions";
import { useDispatch } from "react-redux";
import { AppFlavour } from "configs/AppFlavours";
import { getGSPACE_WEBHOOKURL, triggerGoogleSpacesAPI } from "helpers/helper";
export interface ScheduledEventDetails {
  eventType: ScheduledEvent.EventType;
  eventName: string;
  startTimeEpoch: number;
  endTimeEpoch: number;
}

export interface EVInstantaneousStopEventDialogProps {
  eventType?: ScheduledEvent.EventType;
  startDate?: Date;
  endDate?: Date;
  scheduledEvent?: ScheduledEvent;
  open: boolean;
  handleClose: (event: any, reason?: string) => void;
}

export const EVInstantaneousStopEventDialog = (
  props: EVInstantaneousStopEventDialogProps
) => {
  const { rc } = useGlobalConfig();
  const { translations, config } = rc;

  const instanteanousTimeGap = parseInt(JSON.parse(config.getConfig('EVENT_MANAGEMENT.EV_INSTANTANEOUS_TIME_GAP'))) + 1 || 1;

  const dropDownMinutes = 5;
  const [showRefreshLoad, setShowRefreshLoad] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedMinutes, setSelectedMinutes] = useState(dropDownMinutes);

  // Function to handle dropdown change
  const handleSelectChange = (e : any) => {
    setSelectedMinutes(parseInt(e.target.value));
  };

  const params: any = {
    pilotId: rc.session.getPilotId(),
  };

  let [reload, setReload] = useState(0);

  const { data: evLoadData, error: evLoadError, isLoading: evIsLoading } = useRequestApiData<
  GetEVLoadAPI.retval | any
  >(EVLoadManagementAPI.fetchEVLoad, rc, params, [reload]);

  const theme = useTheme();

  const [{ loading, data, error }, setApiState] = useState<any>({
    loading: false,
    data: null,
    error: null,
  });

  const handleSubmit = async (event : any) => {
    event.preventDefault();
    setShowConfirmation(false);
    props.handleClose(true);

    let resp;

    //Add time difference to start and end date to get corressponding timezone Date.
    const instanteanousTimeGapDelay = instanteanousTimeGap === 0 ? 1 : instanteanousTimeGap;
    const nextRoundedTime = new Date(Math.ceil(new Date().getTime() / (60 * 1000)) * (60 * 1000));
    const evenStartTime = rc.dateUtils.addMinutesToDate(nextRoundedTime, instanteanousTimeGapDelay);
    const eventEndTime = rc.dateUtils.addMinutesToDate(nextRoundedTime, instanteanousTimeGapDelay + selectedMinutes);

    let params = {
      pilotId: rc.session.getPilotId(),
      eventType: ScheduledEvent.EventType.EV_CPE ,
      eventName: 'Instantaneous_EV_Stop_Event_'+ evenStartTime.getTime(),
      startTimeEpoch: Math.round(evenStartTime!.getTime() / 1000),
      endTimeEpoch: Math.round(eventEndTime!.getTime() / 1000),
      timezoneOffsetMins: rc.dateUtils.getTimezoneOffset()
    };

    rc.analytics.trackEvent("SCHEDULE_EVENT_CONFIRM_CLICKED", params);
    resp = await EventManagementAPI.createEvent(rc, params, instanteanousTimeGap < 2 ? false : true);

    // Trigger Google Spaces API
    if (resp) {
      const message = `Instanatenous Event is scheduled by Pilot *${rc.session.getPilotId()}*. 
      Start Time: *${new Date(params.startTimeEpoch * 1000)}*
      End Time: *${new Date(params.endTimeEpoch * 1000)}*
      Status: *${resp.error ? "Failed" : "Success"}*
      Response: *${JSON.stringify(resp)}*
      Environment: *${AppFlavour.getEnvironmentFromURL(window.location.host)}*`;
      const webhookUrl = getGSPACE_WEBHOOKURL(params.eventType);
      triggerGoogleSpacesAPI(message, webhookUrl);
    }

    if (resp.error) {
      toast.error(resp.error?.message || 'Failed to schedule event. Please try again.')
      setApiState({ loading: false, error: resp.error, data: null });
      rc.analytics.trackEvent("SCHEDULE_EVENT_FAILED", params);
    } else {
      toast.success('Instanteanous Event scheduled successfully.')
      setApiState({ loading: false, error: null, data: resp.data });

      if (props.scheduledEvent) {
        if (
          props.scheduledEvent.eventStatus ===
          ScheduledEvent.EventStatus.ONGOING
        ) {
          rc.events.broadcast(EVENTS.RELOAD_ONGOING_EVENTS);
          return;
        }
      }
      rc.events.broadcast(EVENTS.RELOAD_SCHEDULED_EVENTS);
    }
  };

  const [showBglLoader, setShowBglLoader] = useState(false);

  useEffect(()=>{
    if(evLoadData?.EV.lastSyncDTO.refreshLeft <= 0){
      toast.error(translations.getTranslation(EV_LOAD_STRINGS.REFRESH_WARNING));
      setShowRefreshLoad(false);
    }
  },[evLoadData])

  const dispatch = useDispatch();
  const handleEVBackgrounRefresh = () => {
      dispatch(EVLOAD_ACTIONS.REFRESH_EV_LOAD(true));
  }

  return showRefreshLoad && !evIsLoading ? (
    <EVLoadManagement
      scheduleInstantEvent={showRefreshLoad}
      handleClose={() => props.handleClose(true)}
      handleReloadCompleted={() => {
        setShowRefreshLoad(false);
        setShowBglLoader(true);
        setTimeout(() => {
          setReload(++reload);
          setShowBglLoader(false);
          handleEVBackgrounRefresh();
        }, 45_000);
      }}
    />
  ) : (
    <BglDialog
      disableEscapeKeyDown
      onClose={props.handleClose}
      aria-labelledby="customized-dialog-title"
      open={!showRefreshLoad}
    >
      {showBglLoader ? (
        <BglLoading className={styles.loadingCont} />
      ) : showConfirmation ? (
        <>
          <form className={styles.formStyling} onSubmit={handleSubmit}>
            <DialogContent>
              <WarningOutlinedIcon
                fontSize="large"
                sx={{ color: "red" }}
              ></WarningOutlinedIcon>
            </DialogContent>
            <BglDialogTitle
              title={rc.utils.replaceTokens(
                translations.getTranslation(
                  EV_INST_EVENT_STRINGS.STOP_FOR_MINUTES
                ),
                { selectedMinutes: selectedMinutes.toString() }
              )}
              onClose={props.handleClose}
            />

            <Typography sx={{ margin: "0px 20px 20px 20px" }}>
              {translations.getTranslation(EV_INST_EVENT_STRINGS.DESC)}
            </Typography>

            <BglDialogActions>
              <div className={styles.btnCont}>
                <BglCloseButton
                  sx={{ minWidth: "100px", backgroundColor: "red" }}
                  type="submit"
                >
                  {translations.getTranslation(
                    EV_INST_EVENT_STRINGS.BTN.CONFIRM
                  )}
                </BglCloseButton>

                <BglOutlinedButton
                  sx={{
                    minWidth: "100px",
                    color: "#3498db",
                    borderColor: "#3498db",
                  }}
                  onClick={props.handleClose}
                >
                  {translations.getTranslation(
                    EV_INST_EVENT_STRINGS.BTN.CANCEL
                  )}
                </BglOutlinedButton>
              </div>
            </BglDialogActions>
          </form>
        </>
      ) : (
        <>
          <BglDialogTitle
            title={translations.getTranslation(
              EV_INST_EVENT_STRINGS.POPUP_TITLE
            )}
            onClose={props.handleClose}
          />

          <form className={styles.formStyling}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="body1" sx={{ width: "200px" }}>
                    {translations.getTranslation(
                      EV_INST_EVENT_STRINGS.LABEL.CONTROLLABLE_EV
                    )}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: "bold", ml: 2 }}
                  >
                    {(evLoadData?.EV?.countDetails?.controllableCount !==
                    undefined
                      ? evLoadData?.EV?.countDetails?.controllableCount
                      : "?") +
                      translations.getTranslation(
                        EV_INST_EVENT_STRINGS.UNIT.CONTROLLABLE_EV
                      )}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="body1" sx={{ width: "200px" }}>
                    {translations.getTranslation(
                      EV_INST_EVENT_STRINGS.LABEL.DEMAND_DROP
                    )}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: "bold", ml: 2 }}
                  >
                    {(evLoadData?.EV?.loadDetails?.controllableLoad !==
                    undefined
                      ? evLoadData?.EV?.loadDetails?.controllableLoad
                      : "?") +
                      translations.getTranslation(
                        EV_INST_EVENT_STRINGS.UNIT.DEMAND_DROP
                      )}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="body1" sx={{ width: "200px" }}>
                    {translations.getTranslation(
                      EV_INST_EVENT_STRINGS.LABEL.LAST_SYNC_AT
                    )}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: "bold", ml: 2 }}
                  >
                    {evLoadData?.EV?.lastSyncDTO?.lastSync !== undefined
                      ? evLoadData?.EV?.lastSyncDTO?.lastSync
                      : "?"}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="body1" sx={{ width: "200px" }}>
                    {translations.getTranslation(
                      EV_INST_EVENT_STRINGS.LABEL.SELECT_MINUTES
                    )}
                  </Typography>
                  <FormControl>
                    <Select
                      value={selectedMinutes}
                      onChange={handleSelectChange}
                      variant="outlined"
                      sx={{ width: "200px" }}
                    >
                      {[...Array(16)].map((_, index) => (
                        <MenuItem key={index + dropDownMinutes} value={index + dropDownMinutes}>
                          {index + dropDownMinutes}{" "}
                          {translations.getTranslation(
                            EV_INST_EVENT_STRINGS.UNIT.SELECT_MINUTES
                          )}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
              <Grid item xs={12} sx={{ marginTop: "50px" }}>
                <div>
                  <div>
                    <Typography variant="body2" sx={{ margin: "5px 0px" }}>
                      {rc.utils.replaceTokens(
                        translations.getTranslation(
                          EV_INST_EVENT_STRINGS.SCHEDULE_INFO
                        ),
                        { instanteanousTimeGap: (instanteanousTimeGap + 1) }
                      )}
                    </Typography>

                    <div className={styles.btnCont}>
                      <BglPrimaryButton
                        sx={{ width: "100%" }}
                        onClick={() => setShowConfirmation(true)}
                      >
                        {translations.getTranslation(
                          EV_INST_EVENT_STRINGS.BTN.STOP_EV
                        )}
                      </BglPrimaryButton>
                    </div>
                  </div>
                </div>
              </Grid>
            </Grid>
          </form>
        </>
      )}
    </BglDialog>
  );
};
